import React, { useEffect, useMemo, useState } from "react";
import CategoryList from "./CategoryList";
import SubcategoryList from "./SubcategoryList";
import CategoryModal from "./CategoryModal";
import SubcategoryModal from "./SubcategoryModal";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import ProductController from "../controllers/ProductController";
import { userService } from "../services/userService";
import { toast } from "react-toastify";
import Utils from "../utils/Utils";

const createLocalId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const normalizeSubcategory = (subcategory, category = {}) => ({
  subCategoryId:
    subcategory?.subCategoryId || subcategory?.id || createLocalId("sub"),
  subCategoryName: subcategory?.subCategoryName || subcategory?.name || "",
  subCategoryOrder: subcategory?.subCategoryOrder ?? subcategory?.order ?? null,
  description: subcategory?.description || "",
  categoryId: subcategory?.categoryId || category?.categoryId || "",
  categoryName: subcategory?.categoryName || category?.categoryName || "",
  visible: subcategory?.visible ?? subcategory?.displayToUsers ?? true,
});

const normalizeCategory = (category) => {
  const normalizedCategory = {
    categoryId: category?.categoryId || category?.id || createLocalId("cat"),
    categoryName: category?.categoryName || category?.name || "",
    categoryOrder: category?.categoryOrder ?? category?.order ?? null,
    description: category?.description || "",
    visible: category?.visible ?? category?.displayToUsers ?? true,
    subCategories: [],
  };

  normalizedCategory.subCategories = (category?.subCategories || [])
    .filter(Boolean)
    .map((subcategory) =>
      normalizeSubcategory(subcategory, normalizedCategory),
    );

  return normalizedCategory;
};

const getCategoryOrderKey = (categories) =>
  categories.map((category) => category.categoryId).join("|");

const getSubcategoryOrderKey = (subcategories = []) =>
  subcategories.map((subcategory) => subcategory.subCategoryId).join("|");

const buildCategoryUpdateDto = (category, index) => ({
  categoryId: category.categoryId,
  categoryName: category.categoryName,
  categoryOrder: index + 1,
  description: category.description || "",
  displayToUsers: Boolean(category.visible),
});

const buildSubcategoryUpdateDto = (subcategory, category, index) => ({
  categoryId: subcategory.categoryId || category?.categoryId || "",
  description: subcategory.description || "",
  displayToUsers: Boolean(subcategory.visible),
  subCategoryId: subcategory.subCategoryId,
  subCategoryName: subcategory.subCategoryName,
  subCategoryOrder: index + 1,
});

function CategorySettings() {
  const [categories, setCategories] = useState([]);
  const [savedCategoryOrderKey, setSavedCategoryOrderKey] = useState("");
  const [savedSubcategoryOrderKeys, setSavedSubcategoryOrderKeys] = useState(
    {},
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");
  const [subcategoriesLoading, setSubcategoriesLoading] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);
  const [categoryDeleting, setCategoryDeleting] = useState(false);
  const [subcategorySaving, setSubcategorySaving] = useState(false);
  const [subcategoryDeleting, setSubcategoryDeleting] = useState(false);
  const [categoryModal, setCategoryModal] = useState({
    open: false,
    mode: "add",
    category: null,
  });
  const [subcategoryModal, setSubcategoryModal] = useState({
    open: false,
    mode: "add",
    subcategory: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    type: "",
    item: null,
  });

  const selectedCategory = useMemo(
    () =>
      categories.find((category) => category.categoryId === selectedCategoryId),
    [categories, selectedCategoryId],
  );
  const categoryOrderKey = useMemo(
    () => getCategoryOrderKey(categories),
    [categories],
  );
  const isCategoryOrderDirty =
    Boolean(savedCategoryOrderKey) &&
    categoryOrderKey !== savedCategoryOrderKey;
  const subcategoryOrderKey = useMemo(
    () => getSubcategoryOrderKey(selectedCategory?.subCategories),
    [selectedCategory],
  );
  const savedSubcategoryOrderKey =
    savedSubcategoryOrderKeys[selectedCategoryId] || "";
  const isSubcategoryOrderDirty =
    Boolean(selectedCategoryId) &&
    Boolean(savedSubcategoryOrderKey) &&
    subcategoryOrderKey !== savedSubcategoryOrderKey;

  const categoryNameExists = (name, currentCategoryId = "") =>
    categories.some(
      (category) =>
        category.categoryId !== currentCategoryId &&
        category.categoryName.trim().toLowerCase() ===
          name.trim().toLowerCase(),
    );

  const subcategoryNameExists = (name, currentSubcategoryId = "") =>
    selectedCategory?.subCategories.some(
      (subcategory) =>
        subcategory.subCategoryId !== currentSubcategoryId &&
        subcategory.subCategoryName.trim().toLowerCase() ===
          name.trim().toLowerCase(),
    );

  const closeCategoryModal = () =>
    setCategoryModal({ open: false, mode: "add", category: null });

  const closeSubcategoryModal = () =>
    setSubcategoryModal({ open: false, mode: "add", subcategory: null });

  const closeDeleteModal = () =>
    setDeleteModal({ open: false, type: "", item: null });

  const refreshCategories = async () => {
    try {
      const getCategData = await ProductController.getCategories({
        Authorization: userService.getUser(),
      });

      if (Array.isArray(getCategData?.data)) {
        const normalizedCategories = getCategData.data.map(normalizeCategory);
        setCategories(normalizedCategories);
        setSavedCategoryOrderKey(getCategoryOrderKey(normalizedCategories));
        setSavedSubcategoryOrderKeys(
          normalizedCategories.reduce((keys, category) => {
            keys[category.categoryId] = getSubcategoryOrderKey(
              category.subCategories,
            );
            return keys;
          }, {}),
        );
      }
    } catch (error) {
      console.log("error getting categories", error);
    }
  };

  const saveCategory = async ({ name, description, visible }) => {
    const categoryId =
      categoryModal.mode === "edit" ? categoryModal.category?.categoryId : "";
    const categoryIndex = categories.findIndex(
      (category) => category.categoryId === categoryId,
    );
    const categoryRequest = {
      categoryId,
      categoryName: name,
      description,
      displayToUsers: visible,
    };
    const updateRequest = [
      buildCategoryUpdateDto(
        {
          ...categoryModal.category,
          categoryId,
          categoryName: name,
          description,
          visible,
        },
        categoryIndex >= 0 ? categoryIndex : categories.length,
      ),
    ];

    try {
      setCategorySaving(true);
      const response =
        categoryModal.mode === "edit"
          ? await ProductController.updateCategory({
              Authorization: userService.getUser(),
              request: updateRequest,
            })
          : await ProductController.addCategory({
              Authorization: userService.getUser(),
              request: categoryRequest,
            });

      if (Utils.isSuccessResponse(response)) {
        closeCategoryModal();
        await refreshCategories();
        const savedId = response?.data?.categoryId || categoryId;
        if (savedId) setSelectedCategoryId(savedId);
        setSelectedSubCategoryId("");
        toast.success(
          categoryModal.mode === "edit" ? "Category updated" : "Category added",
        );
      } else {
        toast.error(response?.message || "Category could not be saved");
      }
    } catch (error) {
      console.log("error saving category", error);
      toast.error("Category could not be saved");
    } finally {
      setCategorySaving(false);
    }
  };

  const saveSubcategory = async ({ name, description, visible }) => {
    if (!selectedCategory) return;

    const subCategoryId =
      subcategoryModal.mode === "edit"
        ? subcategoryModal.subcategory?.subCategoryId
        : "";
    const subcategoryIndex = selectedCategory.subCategories.findIndex(
      (subcategory) => subcategory.subCategoryId === subCategoryId,
    );
    const request = {
      categoryId: selectedCategory.categoryId,
      description,
      displayToUsers: visible,
      subCategoryName: name,
    };
    if (subcategoryModal.mode === "edit") {
      request.subCategoryId = subCategoryId;
    }
    const updateRequest = [
      buildSubcategoryUpdateDto(
        {
          ...subcategoryModal.subcategory,
          categoryId: selectedCategory.categoryId,
          description,
          visible,
          subCategoryId,
          subCategoryName: name,
        },
        selectedCategory,
        subcategoryIndex >= 0
          ? subcategoryIndex
          : selectedCategory.subCategories.length,
      ),
    ];

    try {
      setSubcategorySaving(true);
      const response =
        subcategoryModal.mode === "edit"
          ? await ProductController.updateSubCategory({
              Authorization: userService.getUser(),
              request: updateRequest,
            })
          : await ProductController.addSubCategory({
              Authorization: userService.getUser(),
              request,
            });

      if (Utils.isSuccessResponse(response)) {
        closeSubcategoryModal();
        await loadSubcategoriesByCategoryId(selectedCategory.categoryId);
        const savedId = response?.data?.subCategoryId || subCategoryId;
        if (savedId) setSelectedSubCategoryId(savedId);
        toast.success(
          subcategoryModal.mode === "edit"
            ? "Subcategory updated"
            : "Subcategory added",
        );
      } else {
        toast.error(response?.message || "Subcategory could not be saved");
      }
    } catch (error) {
      console.log("error saving subcategory", error);
      toast.error("Subcategory could not be saved");
    } finally {
      setSubcategorySaving(false);
    }
  };

  const deleteCategory = async () => {
    if (!deleteModal.item) return;

    try {
      setCategoryDeleting(true);
      const response = await ProductController.deleteCategory({
        Authorization: userService.getUser(),
        categoryId: deleteModal.item.categoryId,
      });

      if (Utils.isSuccessResponse(response)) {
        if (selectedCategoryId === deleteModal.item.categoryId) {
          setSelectedCategoryId("");
          setSelectedSubCategoryId("");
        }
        closeDeleteModal();
        await refreshCategories();
        toast.success("Category deleted");
      } else {
        toast.error(response?.message || "Category could not be deleted");
      }
    } catch (error) {
      console.log("error deleting category", error);
      toast.error("Category could not be deleted");
    } finally {
      setCategoryDeleting(false);
    }
  };

  const deleteSubcategory = async () => {
    if (!selectedCategory || !deleteModal.item) return;

    try {
      setSubcategoryDeleting(true);
      const response = await ProductController.deleteSubCategory({
        Authorization: userService.getUser(),
        subCategoryId: deleteModal.item.subCategoryId,
      });

      if (Utils.isSuccessResponse(response)) {
        if (selectedSubCategoryId === deleteModal.item.subCategoryId) {
          setSelectedSubCategoryId("");
        }
        closeDeleteModal();
        await loadSubcategoriesByCategoryId(selectedCategory.categoryId);
        toast.success("Subcategory deleted");
      } else {
        toast.error(response?.message || "Subcategory could not be deleted");
      }
    } catch (error) {
      console.log("error deleting subcategory", error);
      toast.error("Subcategory could not be deleted");
    } finally {
      setSubcategoryDeleting(false);
    }
  };

  const confirmDelete = () => {
    if (deleteModal.type === "category") {
      deleteCategory();
      return;
    }

    deleteSubcategory();
  };

  const toggleCategoryVisibility = async (categoryId) => {
    const categoryIndex = categories.findIndex(
      (item) => item.categoryId === categoryId,
    );
    const category = categories[categoryIndex];
    if (!category) return;

    const request = [
      buildCategoryUpdateDto(
        {
          ...category,
          visible: !category.visible,
        },
        categoryIndex,
      ),
    ];

    try {
      const response = await ProductController.updateCategory({
        Authorization: userService.getUser(),
        request,
      });

      if (Utils.isSuccessResponse(response)) {
        setCategories((prev) =>
          prev.map((item) =>
            item.categoryId === categoryId
              ? { ...item, visible: !item.visible }
              : item,
          ),
        );
      } else {
        toast.error(
          response?.message || "Category display could not be updated",
        );
      }
    } catch (error) {
      console.log("error updating category display", error);
      toast.error("Category display could not be updated");
    }
  };

  const toggleSubcategoryVisibility = async (subcategoryId) => {
    if (!selectedCategory) return;

    const subcategory = selectedCategory.subCategories.find(
      (item) => item.subCategoryId === subcategoryId,
    );
    if (!subcategory) return;
    const subcategoryIndex = selectedCategory.subCategories.findIndex(
      (item) => item.subCategoryId === subcategoryId,
    );

    const request = [
      buildSubcategoryUpdateDto(
        {
          ...subcategory,
          visible: !subcategory.visible,
        },
        selectedCategory,
        subcategoryIndex,
      ),
    ];

    try {
      const response = await ProductController.updateSubCategory({
        Authorization: userService.getUser(),
        request,
      });

      if (Utils.isSuccessResponse(response)) {
        setCategories((prev) =>
          prev.map((category) =>
            category.categoryId === selectedCategory.categoryId
              ? {
                  ...category,
                  subCategories: category.subCategories.map((item) =>
                    item.subCategoryId === subcategoryId
                      ? { ...item, visible: !item.visible }
                      : item,
                  ),
                }
              : category,
          ),
        );
      } else {
        toast.error(
          response?.message || "Subcategory display could not be updated",
        );
      }
    } catch (error) {
      console.log("error updating subcategory display", error);
      toast.error("Subcategory display could not be updated");
    }
  };

  const loadSubcategoriesByCategoryId = async (categoryId) => {
    if (!categoryId) return;

    const category = categories.find((item) => item.categoryId === categoryId);
    // const request = { categoryId };

    try {
      setSubcategoriesLoading(true);
      const response = await ProductController.getAdminSubCategoriesByCategory({
        Authorization: userService.getUser(),
        categoryId,
      });

      if (Array.isArray(response?.data)) {
        const normalizedSubcategories = response.data.map((subcategory) =>
          normalizeSubcategory(subcategory, category),
        );
        setCategories((prev) =>
          prev.map((item) =>
            item.categoryId === categoryId
              ? {
                  ...item,
                  subCategories: normalizedSubcategories,
                }
              : item,
          ),
        );
        setSavedSubcategoryOrderKeys((prev) => ({
          ...prev,
          [categoryId]: getSubcategoryOrderKey(normalizedSubcategories),
        }));
      }
    } catch (error) {
      console.log("error getting subcategories", error);
    } finally {
      setSubcategoriesLoading(false);
    }
  };

  const selectCategory = (categoryId) => {
    setSelectedCategoryId(categoryId);
    setSelectedSubCategoryId("");
    loadSubcategoriesByCategoryId(categoryId);
  };

  const reorderCategories = (fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return;

    setCategories((prev) => {
      if (fromIndex >= prev.length || toIndex >= prev.length) return prev;

      const next = [...prev];
      const [movedCategory] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, movedCategory);
      return next;
    });
  };

  const uploadCategoryOrder = async () => {
    const request = categories.map(buildCategoryUpdateDto);

    try {
      setCategorySaving(true);
      const response = await ProductController.updateCategory({
        Authorization: userService.getUser(),
        request,
      });

      if (Utils.isSuccessResponse(response)) {
        const orderedCategories = categories.map((category, index) => ({
          ...category,
          categoryOrder: index + 1,
        }));

        setCategories(orderedCategories);
        setSavedCategoryOrderKey(getCategoryOrderKey(orderedCategories));
        toast.success("Category order updated");
      } else {
        toast.error(response?.message || "Category order could not be updated");
      }
    } catch (error) {
      console.log("error updating category order", error);
      toast.error("Category order could not be updated");
    } finally {
      setCategorySaving(false);
    }
  };

  const reorderSubcategories = (fromIndex, toIndex) => {
    if (
      !selectedCategoryId ||
      fromIndex === toIndex ||
      fromIndex < 0 ||
      toIndex < 0
    ) {
      return;
    }

    setCategories((prev) =>
      prev.map((category) => {
        if (category.categoryId !== selectedCategoryId) return category;
        if (
          fromIndex >= category.subCategories.length ||
          toIndex >= category.subCategories.length
        ) {
          return category;
        }

        const nextSubcategories = [...category.subCategories];
        const [movedSubcategory] = nextSubcategories.splice(fromIndex, 1);
        nextSubcategories.splice(toIndex, 0, movedSubcategory);

        return {
          ...category,
          subCategories: nextSubcategories,
        };
      }),
    );
  };

  const uploadSubcategoryOrder = async () => {
    if (!selectedCategory) return;

    const request = selectedCategory.subCategories.map((subcategory, index) =>
      buildSubcategoryUpdateDto(subcategory, selectedCategory, index),
    );

    try {
      setSubcategorySaving(true);
      const response = await ProductController.updateSubCategory({
        Authorization: userService.getUser(),
        request,
      });

      if (Utils.isSuccessResponse(response)) {
        const orderedSubcategories = selectedCategory.subCategories.map(
          (subcategory, index) => ({
            ...subcategory,
            subCategoryOrder: index + 1,
          }),
        );

        setCategories((prev) =>
          prev.map((category) =>
            category.categoryId === selectedCategory.categoryId
              ? { ...category, subCategories: orderedSubcategories }
              : category,
          ),
        );
        setSavedSubcategoryOrderKeys((prev) => ({
          ...prev,
          [selectedCategory.categoryId]:
            getSubcategoryOrderKey(orderedSubcategories),
        }));
        toast.success("Subcategory order updated");
      } else {
        toast.error(
          response?.message || "Subcategory order could not be updated",
        );
      }
    } catch (error) {
      console.log("error updating subcategory order", error);
      toast.error("Subcategory order could not be updated");
    } finally {
      setSubcategorySaving(false);
    }
  };

  useEffect(() => {
    refreshCategories();
  }, []);
  return (
    <div className="category-settings">
      <CategoryList
        categories={categories}
        selectedCategoryId={selectedCategoryId}
        onAddCategory={() =>
          setCategoryModal({ open: true, mode: "add", category: null })
        }
        onSelectCategory={selectCategory}
        onToggleVisibility={toggleCategoryVisibility}
        onEditCategory={(category) =>
          setCategoryModal({ open: true, mode: "edit", category })
        }
        onDeleteCategory={(category) =>
          setDeleteModal({ open: true, type: "category", item: category })
        }
        onReorderCategories={reorderCategories}
        orderDirty={isCategoryOrderDirty}
        onUploadOrder={uploadCategoryOrder}
      />

      <SubcategoryList
        category={selectedCategory}
        selectedSubCategoryId={selectedSubCategoryId}
        loading={subcategoriesLoading}
        onAddSubcategory={() =>
          setSubcategoryModal({
            open: true,
            mode: "add",
            subcategory: null,
          })
        }
        onSelectSubcategory={setSelectedSubCategoryId}
        onToggleVisibility={toggleSubcategoryVisibility}
        onEditSubcategory={(subcategory) =>
          setSubcategoryModal({ open: true, mode: "edit", subcategory })
        }
        onDeleteSubcategory={(subcategory) =>
          setDeleteModal({ open: true, type: "subcategory", item: subcategory })
        }
        onReorderSubcategories={reorderSubcategories}
        orderDirty={isSubcategoryOrderDirty}
        onUploadOrder={uploadSubcategoryOrder}
      />

      {categoryModal.open ? (
        <CategoryModal
          mode={categoryModal.mode}
          category={categoryModal.category}
          isNameTaken={categoryNameExists}
          onSave={saveCategory}
          onClose={closeCategoryModal}
          saving={categorySaving}
        />
      ) : null}

      {subcategoryModal.open && selectedCategory ? (
        <SubcategoryModal
          mode={subcategoryModal.mode}
          subcategory={subcategoryModal.subcategory}
          isNameTaken={subcategoryNameExists}
          onSave={saveSubcategory}
          onClose={closeSubcategoryModal}
          saving={subcategorySaving}
        />
      ) : null}

      {deleteModal.open ? (
        <DeleteConfirmationModal
          type={deleteModal.type}
          item={deleteModal.item}
          onConfirm={confirmDelete}
          onClose={closeDeleteModal}
          loading={
            deleteModal.type === "category"
              ? categoryDeleting
              : subcategoryDeleting
          }
        />
      ) : null}
    </div>
  );
}

export default CategorySettings;
